<script>
const FEATURE_INITIAL = {
  id: null,
  key: "",
  type: "text",
  parent_id: null,
  group_id: null,
  show_on_value: "",
  description: "",
  optionsText: "",
}

export default {
  name: "FeatureEdit",
  props: {
    agentId: [Number, String],
    isActive: Boolean,
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      features: [],
      form: { ...FEATURE_INITIAL },
      errors: {},
      featureTypes: ["select", "text", "number"],
      selectedGroupId: null,
      groupDialogIsVisible: false,
      featureDialogIsVisible: false,
      groupForm: {
        id: null,
        key: "",
        description: "",
      },
      groupErrors: {},
      isGroupSaving: false,
    }
  },
  computed: {
    groups() {
      return this.features.filter(item => item.type === "group")
    },
    isSelectType() {
      return this.form.type === "select"
    },
    selectedGroupFeatures() {
      if (!this.selectedGroupId) return []
      return this.features.filter(item => item.group_id === this.selectedGroupId && item.type !== "group")
    },
    formGroupFeatures() {
      const currentGroupId = this.form.group_id || this.selectedGroupId
      if (!currentGroupId) return []
      return this.features.filter(item => item.group_id === currentGroupId && item.type !== "group")
    },
    parentCandidates() {
      return this.formGroupFeatures.filter(item => item.type === "select" && item.id !== this.form.id)
    },
  },
  watch: {
    isActive(newValue) {
      if (newValue) this.getFeatures()
    },
  },
  methods: {
    flattenFeatureTree(feature) {
      const children = Array.isArray(feature.children) ? feature.children : []
      const groupItems = Array.isArray(feature.group_items) ? feature.group_items : []
      return [
        feature,
        ...groupItems.flatMap(item => this.flattenFeatureTree(item)),
        ...children.flatMap(item => this.flattenFeatureTree(item)),
      ]
    },
    async getFeatures() {
      this.isLoading = true
      try {
        const { data } = await this.$axios.get(`admin/agents/${this.agentId}/features`)
        const groups = data?.data || []
        this.features = groups.flatMap(item => this.flattenFeatureTree(item))
        if (!this.selectedGroupId && this.groups.length) this.selectedGroupId = this.groups[0].id
        if (this.selectedGroupId && !this.groups.find(item => item.id === this.selectedGroupId)) {
          this.selectedGroupId = this.groups.length ? this.groups[0].id : null
        }
      } catch (e) {
        throw new Error(e)
      } finally {
        this.isLoading = false
      }
    },
    getGroupFeatureRows(groupId) {
      const items = this.features.filter(item => item.group_id === groupId && item.type !== "group")
      const byParent = items.reduce((acc, item) => {
        const key = item.parent_id || 0
        if (!acc[key]) acc[key] = []
        acc[key].push(item)
        return acc
      }, {})

      const visit = (parentId = 0, level = 0) => {
        const children = byParent[parentId] || []
        return children.flatMap(item => [
          { ...item, _level: level, _parentKey: items.find(i => i.id === item.parent_id)?.key || null },
          ...visit(item.id, level + 1),
        ])
      }

      return visit(0, 0)
    },
    selectGroup(groupId) {
      this.selectedGroupId = groupId
    },
    openCreateGroup() {
      this.groupErrors = {}
      this.groupForm = { id: null, key: "", description: "" }
      this.groupDialogIsVisible = true
    },
    openEditGroup(group) {
      this.groupErrors = {}
      this.groupForm = {
        id: group.id,
        key: group.key || "",
        description: group.description || "",
      }
      this.groupDialogIsVisible = true
    },
    closeGroupDialog() {
      this.groupDialogIsVisible = false
      this.groupErrors = {}
      this.groupForm = { id: null, key: "", description: "" }
    },
    async saveGroup() {
      this.groupErrors = {}
      this.isGroupSaving = true
      const payload = {
        key: this.groupForm.key,
        type: "group",
        description: this.groupForm.description || null,
      }
      try {
        if (this.groupForm.id) {
          await this.$axios.put(`admin/agents/${this.agentId}/features/${this.groupForm.id}`, payload)
        } else {
          await this.$axios.post(`admin/agents/${this.agentId}/features`, payload)
        }
        await this.getFeatures()
        if (!this.groupForm.id) {
          const createdGroup = [...this.groups].sort((a, b) => b.id - a.id)[0]
          if (createdGroup) this.selectedGroupId = createdGroup.id
        }
        this.closeGroupDialog()
      } catch (e) {
        this.groupErrors = e.error?.response?.data?.errors ?? {}
        throw e
      } finally {
        this.isGroupSaving = false
      }
    },
    openCreateFeature(groupId) {
      this.resetForm()
      this.form.group_id = groupId
      this.featureDialogIsVisible = true
    },
    openEditFeature(feature) {
      this.errors = {}
      const optionsText = this.formatBodyToText(feature?.body)
      this.form = {
        id: feature.id || null,
        key: feature.key || "",
        type: feature.type || "text",
        parent_id: feature.parent_id || null,
        group_id: feature.group_id || null,
        show_on_value: feature.show_on_value || "",
        description: feature.description || "",
        optionsText,
      }
      this.featureDialogIsVisible = true
    },
    closeFeatureDialog() {
      this.featureDialogIsVisible = false
      this.resetForm()
    },
    resetForm() {
      this.errors = {}
      this.form = { ...FEATURE_INITIAL }
    },
    formatBodyToText(body) {
      if (!body) return ""
      if (Array.isArray(body)) return body.join("\n")
      if (typeof body === "object") return Object.values(body).join("\n")
      return ""
    },
    formatTextToBody(text) {
      const rows = (text || "").split("\n").map(item => item.trim()).filter(Boolean)
      return rows
    },
    buildPayload() {
      const payload = {
        key: this.form.key,
        type: this.form.type,
        description: this.form.description || null,
      }
      if (this.form.parent_id) payload.parent_id = this.form.parent_id
      if (this.form.group_id) payload.group_id = this.form.group_id
      payload.show_on_value = this.form.show_on_value || null
      payload.body = this.isSelectType ? this.formatTextToBody(this.form.optionsText) : null
      return payload
    },
    async saveFeatureInput() {
      this.errors = {}
      this.isSaving = true
      const payload = this.buildPayload()
      try {
        if (this.form.id) {
          await this.$axios.put(`admin/agents/${this.agentId}/features/${this.form.id}`, payload)
        } else {
          await this.$axios.post(`admin/agents/${this.agentId}/features`, payload)
        }
        await this.getFeatures()
        this.selectedGroupId = this.form.group_id || this.selectedGroupId
        this.closeFeatureDialog()
      } catch (e) {
        this.errors = e.error?.response?.data?.errors ?? {}
        throw e
      } finally {
        this.isSaving = false
      }
    },
    async deleteFeature(featureId) {
      if (!featureId) return
      await this.$axios.delete(`admin/agents/${this.agentId}/features/${featureId}`)
      await this.getFeatures()
    },
  }
}
</script>

<template>
  <div class="has-background-white p-5 is-relative" style="min-height: 100px">
    <b-loading v-model="isLoading" :is-full-page="false" />
    <div class="is-flex is-justify-content-space-between is-align-items-center mb-5">
      <h2 class="is-size-5 has-text-weight-semibold">Features</h2>
      <b-button
        type="is-info"
        size="is-small"
        :disabled="isLoading"
        @click="openCreateGroup"
      >+ Добавить feature</b-button>
    </div>

    <div class="columns is-multiline">
      <div
        v-for="group in groups"
        :key="group.id"
        class="column is-full"
      >
        <div
          class="feature-group p-4"
          @click="selectGroup(group.id)"
        >
          <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
            <h3 class="is-size-6 has-text-weight-semibold">{{ group.description || group.key }}</h3>
            <div class="is-flex is-align-items-center">
              <b-button class="mr-2" type="is-info is-light" size="is-small" icon-right="edit" @click.stop="openEditGroup(group)" />
              <b-button type="is-info is-light" size="is-small" @click.stop="openCreateFeature(group.id)">+ Добавить инпут</b-button>
            </div>
          </div>

          <b-table
            :data="getGroupFeatureRows(group.id)"
            hoverable
            striped
            narrowed
            @cellclick="openEditFeature"
          >
            <b-table-column field="description" width="40%" label="Description" v-slot="props">
              <div class="input-key-cell is-ellipsis">{{ props.row.description || "-" }}</div>
            </b-table-column>
            <b-table-column field="key" width="35%" label="Input key" v-slot="props">
              <div class="input-key-cell is-ellipsis" :style="{ paddingLeft: `${props.row._level * 16}px` }">
                <span v-if="props.row._level > 0" class="mr-1 has-text-grey">↳</span>{{ props.row.key }}
              </div>
            </b-table-column>
            <b-table-column field="type" width="12%" label="Type" v-slot="props">
              {{ props.row.type }}
            </b-table-column>
            <b-table-column width="13%" label="Actions" centered v-slot="props">
              <b-button
                class="mr-2"
                type="is-info is-light"
                size="is-small"
                icon-right="edit"
                @click.stop="openEditFeature(props.row)"
              />
              <b-button
                type="is-danger is-light"
                size="is-small"
                icon-right="trash"
                @click.stop="deleteFeature(props.row.id)"
              />
            </b-table-column>
          </b-table>
        </div>
      </div>
    </div>

    <b-modal :active="groupDialogIsVisible" :can-cancel="true" @close="closeGroupDialog">
      <div class="has-background-white-ter p-4">
        <h3 class="is-size-5 has-text-weight-semibold mb-4">Добавить feature блок</h3>
        <form @submit.prevent="saveGroup">
          <b-field
            label="Key"
            :type="groupErrors.key ? 'is-danger' : ''"
            :message="groupErrors.key && groupErrors.key[0] || ''"
          >
            <b-input v-model="groupForm.key" />
          </b-field>
          <b-field
            label="Description"
            :type="groupErrors.description ? 'is-danger' : ''"
            :message="groupErrors.description && groupErrors.description[0] || ''"
          >
            <b-input v-model="groupForm.description" type="textarea" />
          </b-field>
          <div class="is-flex is-justify-content-end">
            <b-button class="mr-2" :disabled="isGroupSaving" @click="closeGroupDialog">Отмена</b-button>
            <b-button type="is-info" native-type="submit" :loading="isGroupSaving">Сохранить</b-button>
          </div>
        </form>
      </div>
    </b-modal>

    <b-modal :active="featureDialogIsVisible" :can-cancel="true" @close="closeFeatureDialog">
      <div class="has-background-white-ter p-4">
        <h3 class="is-size-5 has-text-weight-semibold mb-4">
          {{ form.id ? "Редактировать input" : "Добавить input" }}
        </h3>
        <form @submit.prevent="saveFeatureInput">
          <div class="columns is-multiline">
            <div class="column is-full">
              <b-field
                label="Input key"
                :type="errors.key ? 'is-danger' : ''"
                :message="errors.key && errors.key[0] || ''"
              >
                <b-input v-model="form.key" />
              </b-field>
            </div>
            <div class="column is-full">
              <b-field
                label="Description"
                :type="errors.description ? 'is-danger' : ''"
                :message="errors.description && errors.description[0] || ''"
              >
                <b-input v-model="form.description" type="textarea" />
              </b-field>
            </div>
            <div class="column is-half">
              <b-field
                label="Type"
                :type="errors.type ? 'is-danger' : ''"
                :message="errors.type && errors.type[0] || ''"
              >
                <b-select v-model="form.type" expanded>
                  <option v-for="type in featureTypes" :key="type" :value="type">{{ type }}</option>
                </b-select>
              </b-field>
            </div>
            <div class="column is-half">
              <b-field
                label="Parent"
                :type="errors.parent_id ? 'is-danger' : ''"
                :message="errors.parent_id && errors.parent_id[0] || ''"
              >
                <b-select v-model="form.parent_id" expanded>
                  <option :value="null">-</option>
                  <option v-for="item in parentCandidates" :key="item.id" :value="item.id">{{ item.key }}</option>
                </b-select>
              </b-field>
            </div>
            <div v-if="form.parent_id" class="column is-full">
              <b-field
                label="Show on value"
                :type="errors.show_on_value ? 'is-danger' : ''"
                :message="errors.show_on_value && errors.show_on_value[0] || ''"
              >
                <b-input v-model="form.show_on_value" placeholder="Например: green" />
              </b-field>
            </div>
            <div class="column is-full">
              <b-field
                label="Group"
                :type="errors.group_id ? 'is-danger' : ''"
                :message="errors.group_id && errors.group_id[0] || ''"
              >
                <b-select v-model="form.group_id" expanded>
                  <option v-for="item in groups" :key="item.id" :value="item.id">{{ item.key }}</option>
                </b-select>
              </b-field>
            </div>
            <div v-if="isSelectType" class="column is-full">
              <b-field
                label="Options (каждая опция с новой строки)"
                :type="errors.body ? 'is-danger' : ''"
                :message="errors.body && errors.body[0] || ''"
              >
                <b-input v-model="form.optionsText" type="textarea" />
              </b-field>
            </div>
            <div class="column is-full is-flex is-justify-content-end">
              <b-button class="mr-2" :disabled="isSaving" @click="closeFeatureDialog">Отмена</b-button>
              <b-button type="is-info" native-type="submit" :loading="isSaving">
                {{ form.id ? "Сохранить" : "Добавить" }}
              </b-button>
            </div>
          </div>
        </form>
      </div>
    </b-modal>
  </div>
</template>

<style scoped>
.feature-group {
  border: 1px solid #dbdbdb;
  border-radius: 8px;
  transition: border-color .2s, box-shadow .2s;
}

.feature-group--active {
  border-color: #3273dc;
  box-shadow: 0 0 0 1px #3273dc inset;
}

.input-key-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
